import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { AnalyticsEventLocation } from '@mb/lib';
import { getUserLang, isEuEnv } from '@mb/lib';
import { useIntl } from 'react-intl';
import * as Styled from './AskProductModalView.styles';
import { SLIDE_DOWN_ANIMATION_MS } from '../../config';
import { definedTranslation } from '../../i18n';
import { ISortedOrders, RepList, IProject, IOrder } from '../../types';
import { getRepList } from '../../utils/getRepList';
import { getUpdatedProjectList } from '../../utils/getUpdatedProjectList';
import {
  AnalyticsEventAPQAction,
  AnalyticsEventName,
  sendAnalyticsEvent,
} from '../../utils/helpers/analytics';
import {
  shouldTriggerAskProductModalDialog,
  updateAskProductModalTimestamp,
} from '../../utils/localStorage';
import { sortOrders } from '../../utils/sortOrders';
import { AskProductModalAllCleared } from '../AskProductModalAllCleared';
import { AskProductOrderList } from '../AskProductOrderList';

const userLang = getUserLang();
const prefix = isEuEnv ? `/${userLang}` : '';

const titleDefault = 'Ready to connect with a Brand Rep?';
const subtitleDefault = 'Select an option below to message them directly';

interface IAskProductModalViewProps {
  orders: IOrder[];
  lastOrderCutoff: number;
  isForcedOpen?: boolean;
  setIsForcedOpen?: (isForcedOpen: boolean) => void;
}

export const AskProductModalView: React.FC<IAskProductModalViewProps> = (
  props,
) => {
  const { orders, lastOrderCutoff, isForcedOpen, setIsForcedOpen } = props;
  const [open, setOpen] = useState(isForcedOpen ?? false);
  const analyticsEventSentForPopupOpen = useRef(false);
  const [sortedOrders, setSortedOrders] = useState<ISortedOrders>();
  const [representatives, setRepresentatives] = useState<RepList>();
  const [hadInteractions, setHadInteractions] = useState(false);
  const [idsToUpdate, setIdsToUpdate] = useState<string[]>([]);
  const [timerId, setTimerId] = useState<NodeJS.Timeout | undefined>(undefined);
  const [allCleared, setAllCleared] = useState(false);
  const [userHasNoOrders, setUserHasNoOrders] = useState(false);
  const intl = useIntl();

  const titles = useMemo(() => {
    return {
      title: intl?.formatMessage(definedTranslation.title) || titleDefault,
      subtitle:
        intl?.formatMessage(definedTranslation.subtitle) || subtitleDefault,
      noOrders: {
        title: intl?.formatMessage(definedTranslation.noOrders.title),
        subtitle: intl?.formatMessage(definedTranslation.noOrders.subtitle),
      },
    };
  }, [intl]);

  const urlParams = new URLSearchParams(window.location.search);
  const isAskProductModalEnabled =
    Boolean(urlParams.get('askproductmodal')) || isForcedOpen;

  const showTodayOrders = Boolean(urlParams.get('showtodayorders'));
  const isRequiredCondition = shouldTriggerAskProductModalDialog();

  useEffect(() => {
    // TODO: I will rewrite this logic when I refactor the entire component for the new design
    if (!hadInteractions && isAskProductModalEnabled && orders.length === 0) {
      setAllCleared(true);
      setUserHasNoOrders(true);
    }
  }, [orders, isAskProductModalEnabled]);

  useEffect(() => {
    if (!isRequiredCondition && !isAskProductModalEnabled) {
      return;
    }

    if (!sortedOrders) {
      setSortedOrders(sortOrders({ orders, showTodayOrders, lastOrderCutoff }));
    }

    setRepresentatives(getRepList(orders));
    setOpen(true);
    updateAskProductModalTimestamp();
  }, [
    orders,
    isRequiredCondition,
    isAskProductModalEnabled,
    showTodayOrders,
    lastOrderCutoff,
    isForcedOpen,
  ]);

  useEffect(() => {
    if (
      open &&
      sortedOrders &&
      Object.keys(sortedOrders.products).length !== 0 &&
      !analyticsEventSentForPopupOpen.current
    ) {
      sendAnalyticsEvent({
        event: AnalyticsEventName.APQ,
        event_location: AnalyticsEventLocation.HOME,
        action: AnalyticsEventAPQAction.OPEN,
      });
      analyticsEventSentForPopupOpen.current = true;
    }
    // Reset the analyticsEventSentForPopupOpen flag when the popup is closed
    else if (!open) {
      analyticsEventSentForPopupOpen.current = false;
    }
  }, [open, sortedOrders]);

  useEffect(() => {
    if (
      hadInteractions &&
      sortedOrders &&
      Object.keys(sortedOrders.products).length === 0
    ) {
      sendAnalyticsEvent({
        event: AnalyticsEventName.APQ,
        event_location: AnalyticsEventLocation.HOME,
        action: AnalyticsEventAPQAction.ALL_CLEARED,
      });
      setAllCleared(true);
    }

    if (
      isAskProductModalEnabled &&
      sortedOrders &&
      Object.keys(sortedOrders.products).length === 0
    ) {
      setAllCleared(true);
    }
  }, [sortedOrders, hadInteractions]);

  const handleOnAction = useCallback((itemId: string) => {
    // @ts-ignore TODO: Fix this type
    splitTrack('user', eventType, value, properties);
    setIdsToUpdate((prevState) => {
      return [...prevState, itemId];
    });
    setHadInteractions(true);
    updateAskProductModalTimestamp();
  }, []);

  const updateStateAndClearIds = useCallback(() => {
    setSortedOrders((prevState: ISortedOrders) => {
      if (!prevState) return;
      const newState = { ...prevState };
      idsToUpdate.forEach((id) => {
        const updatedProjectList = getUpdatedProjectList(newState, id);
        newState.projects = newState.projects.filter((project: IProject) =>
          updatedProjectList.map(({ id }) => id).includes(project.id),
        );
      });
      return newState;
    });
    setIdsToUpdate([]);
  }, [idsToUpdate]);

  useEffect(() => {
    clearTimeout(timerId);

    if (idsToUpdate.length === 0) {
      return;
    }
    const timer = setTimeout(() => {
      updateStateAndClearIds();
      setTimerId(undefined);
    }, SLIDE_DOWN_ANIMATION_MS);

    setTimerId(timer);

    return () => {
      clearTimeout(timer);
    };
  }, [idsToUpdate, updateStateAndClearIds]);

  const forceOrderUpdate = useCallback(() => {
    updateStateAndClearIds();
    clearTimeout(timerId);
  }, [updateStateAndClearIds]);

  const handleOnClose = useCallback(
    // @ts-expect-error // TODO: fix types
    (_, reason: 'backdropClick' | 'escapeKeyDown') => {
      const actionType =
        reason === 'backdropClick'
          ? AnalyticsEventAPQAction.CLICK_OFF
          : AnalyticsEventAPQAction.CLOSE;
      sendAnalyticsEvent({
        event: AnalyticsEventName.APQ,
        event_location: AnalyticsEventLocation.HOME,
        action: actionType,
      });

      setOpen(false);
      forceOrderUpdate();
      setIsForcedOpen && setIsForcedOpen(false);
      updateAskProductModalTimestamp();
    },
    [hadInteractions],
  );

  const handleOnCloseAllCleared = useCallback(() => {
    updateAskProductModalTimestamp();
    sendAnalyticsEvent({
      event: AnalyticsEventName.APQ,
      event_location: AnalyticsEventLocation.HOME,
      action: AnalyticsEventAPQAction.CLOSE,
    });
    window.location.href = `${prefix}/search`;
  }, []);

  if (
    !sortedOrders ||
    (Object.keys(sortedOrders.products).length === 0 && !hadInteractions)
  ) {
    if (!isAskProductModalEnabled) {
      return null;
    }
  }

  return (
    <>
      <Styled.AskProductModalView
        dialogWidth={1034}
        dialogHeight={718}
        open={open}
        onClose={handleOnClose}
        showCloseIcon={true}
      >
        <Styled.Header>
          <Styled.Title variant="h2" component="h5" fontWeight={600}>
            {titles.title}
          </Styled.Title>
          {!allCleared && (
            <Styled.Subtitle variant="h5" component="p" fontWeight={400}>
              {titles.subtitle}
            </Styled.Subtitle>
          )}
        </Styled.Header>

        {allCleared ? (
          <AskProductModalAllCleared
            onButtonClick={handleOnCloseAllCleared}
            title={userHasNoOrders ? titles.noOrders.title : undefined}
            description={userHasNoOrders ? titles.noOrders.subtitle : undefined}
            intl={intl}
          />
        ) : (
          sortedOrders && (
            <AskProductOrderList
              orders={sortedOrders}
              representatives={representatives}
              onActionClick={handleOnAction}
              intl={intl}
            />
          )
        )}
      </Styled.AskProductModalView>
    </>
  );
};
